import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  CircularProgress,
  Typography,
  OutlinedInput,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getGlobalSearchList, setSelectedChat } from "../../lib/redux/actions";
import { Search } from "lucide-react";

export default function SearchChatDialog({
  open,
  onClose,
  closeSidebar, // Assuming you might need this to close the sidebar on mobile after selecting a chat
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const router = useNavigate();
  const dispatch = useDispatch();

  // Implement debounced API call for search
  useEffect(() => {
    if (query.length > 2) {
      // Only search if query is at least 3 characters
      const delayDebounceFn = setTimeout(() => {
        setLoading(true);
        setError(null);
        // Replace with your actual API call

        const payload = {
          query: query,
        };
        getGlobalSearchList(payload)
          .then((data) => {
            setResults(data.data);
            setLoading(false);
          })
          .catch((err) => {
            setError(err.message);
            setLoading(false);
          });
      }, 500); // Debounce for 500ms

      return () => clearTimeout(delayDebounceFn);
    } else {
      setResults([]);
      setError(null);
    }
  }, [query]);

  const handleChatSelect = (chatId) => {
    // Dispatch action to set selected chat and navigate
    dispatch(setSelectedChat({ _id: chatId }));
    router(`/chat/${chatId}`);
    handleClose(); // Close the dialog
    if (closeSidebar) {
      closeSidebar(); // Close the sidebar if on mobile
    }
  };

  function handleClose() {
    setQuery("");
    setResults([]);
    setLoading(false);
    setError(null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Search Chats</DialogTitle>
      <DialogContent
        dividers
        className="!max-h-[400px] overflow-y-auto px-6 "
      >
        <div className="sticky top-0 z-10 bg-white py-4">
          <OutlinedInput
            startAdornment={<Search position="start" className="mr-2" />}
            type="text"
            placeholder="Search chats..."
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {loading && (
          <CircularProgress
            size={20}
            className="mx-auto block my-4"
          />
        )}
        {error && (
          <Typography color="error" className="m-4">
            Error: {error}
          </Typography>
        )}
        <List>
          {results.length > 0
            ? results.map((chat) => (
                <ListItem
                  button
                  key={chat._id}
                  onClick={() => handleChatSelect(chat._id)}
                  className="flex flex-col cursor-pointer"
                >
                  <Typography variant="title" align="left" className="w-full">
                    {chat.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    align="left"
                    className="w-full"
                  >
                    {chat.matchedChat.message.substring(0, 50)}...
                  </Typography>
                </ListItem>
              ))
            : !loading &&
              query.length > 0 &&
              !error && (
                <Typography className="m-4">
                  No results found.
                </Typography>
              )}
        </List>
      </DialogContent>
    </Dialog>
  );
}


SearchChatDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  closeSidebar: PropTypes.func, // Optional
};